// src/lib/api/financial/estimates.js
import { supabase } from '../../supabase'
import { getValidatedCompanyId, getValidatedUserId } from '../shared/helpers'
import { withErrorHandling, createSuccessResponse } from '../shared/errors'
import { validateRequired, sanitizeObject } from '../shared/validation'
import { computeClientTotal, recoverPayoutBase, STRIPE_FEE_RATE, STRIPE_FEE_FIXED } from '@prosperia/shared'

// Snapshot resync (estimates). Mirror of the invoices helper. See that file
// for the full design notes — same single rule: payoutBase is authoritative,
// outputs (total_amount, final_price, client_payment_amount,
// processing_fees_amount) are always derived forward via computeClientTotal.
// Rate-agnostic recovery; cross-check (a)=(services−discount)+tax vs
// (b)=total−surcharge; triggers only on base-affecting fields.
//
// Deposit gate: a deposit charge against an estimate inserts a payments row
// keyed by estimate_id (supabase/functions/stripe-connect-account-webhook/
// index.ts:363-385 in finalizeEstimateDepositAcceptance) and ONLY THEN flips
// estimate.status to 'accepted'/'scheduled'. The two writes are sequential
// not transactional; if the status update fails (logged but not rolled
// back), an open estimate can carry a recorded deposit. Resyncing such a
// row would clobber the payoutBase that deposit was charged against —
// throw with the amount and force product to reconcile.
const round2 = (n) => Math.round(n * 100) / 100
const resyncEstimateSnapshot = async ({ estimateId, companyId, baseChange = {} }) => {
  const { data: existing, error: readErr } = await supabase
    .from('estimates')
    .select('total_amount, processing_fees_amount, services_subtotal, tax_amount, discount_amount, discount_type')
    .eq('id', estimateId)
    .eq('company_id', companyId)
    .single()
  if (readErr || !existing) throw readErr || new Error('Estimate not found for snapshot resync')

  // Deposit gate (estimates ↔ payments via estimate_id; invoice_id may be
  // NULL until the estimate converts and InvoiceFormModal back-fills it).
  const { data: paidRows, error: payErr } = await supabase
    .from('payments')
    .select('amount')
    .eq('estimate_id', estimateId)
    .eq('company_id', companyId)
    .eq('status', 'completed')
  if (payErr) throw payErr
  const alreadyPaid = (paidRows || []).reduce((s, p) => s + Number(p.amount || 0), 0)
  if (alreadyPaid > 0) {
    throw new Error(
      `Refusing to resync estimate ${estimateId}: $${alreadyPaid.toFixed(2)} already paid as deposit. ` +
      `That deposit was charged against the current payoutBase; re-snapshotting would corrupt the basis. ` +
      `Product decision needed: re-snapshot (refund delta) vs. lock the row.`
    )
  }

  const recovered = recoverPayoutBase(existing)
  if (recovered.inconsistent) {
    throw new Error(
      `Estimate ${estimateId} is already inconsistent: ` +
      `services+tax=$${recovered.baseFromParts.toFixed(2)} vs total−surcharge=$${recovered.baseFromTotal.toFixed(2)}. ` +
      `Resync refused — the row needs a manual decision before any edit.`
    )
  }
  const { payoutBase: existingBase, passThrough } = recovered
  const oldServices = Number(existing.services_subtotal) || 0
  const oldTaxAmt = Number(existing.tax_amount) || 0

  const newServices = baseChange.services_subtotal !== undefined
    ? Number(baseChange.services_subtotal) || 0
    : oldServices
  const newTaxAmt = baseChange.tax_amount !== undefined
    ? Number(baseChange.tax_amount) || 0
    : oldTaxAmt
  const baseChanged =
    baseChange.services_subtotal !== undefined ||
    baseChange.tax_amount !== undefined
  const newPayoutBase = baseChanged ? round2(newServices + newTaxAmt) : existingBase

  const { clientTotal, clientSurcharge } = computeClientTotal({
    payoutBase: newPayoutBase,
    passThrough,
  })
  return {
    total_amount: clientTotal,
    final_price: clientTotal,
    client_payment_amount: clientTotal,
    processing_fees_amount: clientSurcharge,
    services_subtotal: newServices,
    tax_amount: newTaxAmt,
  }
}

/**
 * Get all estimates for a company
 * Migrated from supabaseApi.js lines 1863-1880
 */
const _getEstimates = async (companyId = null) => {
  const resolvedCompanyId = companyId || await getValidatedCompanyId()
  if (!resolvedCompanyId) {
    return createSuccessResponse([])
  }

  const { data, error } = await supabase
    .from('estimates')
    .select(`
      *,
      client:clients(name, email, phone, sms_consent, marketing_sms_consent)
    `)
    .eq('company_id', resolvedCompanyId)
    .order('created_at', { ascending: false })
    .limit(500)

  if (error) throw error
  return createSuccessResponse(data || [])
}

/**
 * Create a new estimate
 * Migrated from supabaseApi.js lines 1882-1902
 */
const _createEstimate = async (estimateData) => {
  const companyId = estimateData.company_id || await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  const { data: inserted, error: insertError } = await supabase
    .from('estimates')
    .insert([{
      ...estimateData,
      company_id: companyId,
      created_by: (await getValidatedUserId()) || null
    }])
    .select()
    .single()

  if (insertError) throw insertError

  // Re-fetch with the joined client. We do this as a separate SELECT instead
  // of chaining .select('*, client:clients(...)') after .insert() because the
  // INSERT+SELECT(embed) chain hangs against this project's PostgREST/RLS
  // setup. Bare .select() works on the mutation; the embed works on a pure
  // SELECT — same pattern as _getEstimateById.
  const { data, error } = await supabase
    .from('estimates')
    .select(`
      *,
      client:clients(name, email, phone, sms_consent, marketing_sms_consent)
    `)
    .eq('id', inserted.id)
    .eq('company_id', companyId)
    .single()

  if (error) throw error
  return data
}

/**
 * Create a draft estimate with AI service
 * Creates a draft estimate and adds the AI service as a line item
 */
const _createDraftEstimateWithService = async (serviceData, customerInfo = {}, authContext = null) => {
  try {
    console.log('[createDraftEstimateWithService] Starting with serviceData:', serviceData)
    console.log('[createDraftEstimateWithService] Auth context:', authContext)
    
    let user = null
    let companyId = null
    
    // Use provided auth context if available (avoids hanging auth calls)
    if (authContext?.userId && authContext?.companyId) {
      console.log('[createDraftEstimateWithService] Using provided auth context')
      user = { id: authContext.userId }
      companyId = authContext.companyId
    } else {
      // Fallback to auth lookup. getValidatedUserId prefers the auth-provider
      // cache and only races against supabase.auth.getUser() when cold, which
      // avoids the Web Lock deadlock that hits us after a long tab-out.
      console.log('[createDraftEstimateWithService] Getting user from auth...')
      const userId = await getValidatedUserId()
      console.log('[createDraftEstimateWithService] User:', userId)

      if (!userId) {
        throw new Error('User not authenticated - please sign in again')
      }

      user = { id: userId }
      
      // Direct company lookup without cache
      console.log('[createDraftEstimateWithService] Getting company ID directly...')
      
      // Check employees table first
      const { data: employee } = await supabase
        .from('employees')
        .select('company_id')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .maybeSingle()
      
      if (employee?.company_id) {
        companyId = employee.company_id
        console.log('[createDraftEstimateWithService] Found company ID from employee:', companyId)
      } else {
        // Check if user owns a company
        const { data: company } = await supabase
          .from('companies')
          .select('id')
          .eq('owner_user_id', user.id)
          .maybeSingle()
        
        companyId = company?.id
        console.log('[createDraftEstimateWithService] Found company ID from ownership:', companyId)
      }
    }
    
    console.log('[createDraftEstimateWithService] Final user ID:', user?.id, 'Company ID:', companyId)
    
    if (!user) {
      throw new Error('User not authenticated')
    }

    // Generate estimate number using RPC or direct query
    // Get current counter from company settings
    console.log('[createDraftEstimateWithService] Fetching company settings...')
    const { data: company, error: companyError } = await supabase
      .from('companies')
      .select('settings')
      .eq('id', companyId)
      .single()
    
    console.log('[createDraftEstimateWithService] Company settings:', company?.settings, 'Error:', companyError)
    
    const settings = company?.settings || {}
    const currentCounter = settings.estimate_counter || 0
    const newCounter = currentCounter + 1
    const year = new Date().getFullYear()
    const estimateNumber = `EST-${year}-${String(newCounter).padStart(4, '0')}`
    
    console.log('[createDraftEstimateWithService] Generated estimate number:', estimateNumber)
    
    // Update counter
    console.log('[createDraftEstimateWithService] Updating counter...')
    const { error: updateError } = await supabase
      .from('companies')
      .update({ 
        settings: { 
          ...settings, 
          estimate_counter: newCounter 
        } 
      })
      .eq('id', companyId)
    
    if (updateError) {
      console.error('[createDraftEstimateWithService] Error updating counter:', updateError)
    }

    // Create line item from service data
    const lineItem = {
      description: serviceData.service_name || serviceData.description || 'Service',
      quantity: 1,
      price: serviceData.price,
      taxRate: 0,
      discount: 0
    }
    
    console.log('[createDraftEstimateWithService] Line item:', lineItem)

    // Create draft estimate
    const estimateData = {
      company_id: companyId,
      estimate_number: estimateNumber,
      title: serviceData.service_name || 'AI Generated Estimate',
      estimate_type: 'standard',
      status: 'draft',
      items: [lineItem],
      customer_name: customerInfo.customer_name || '',
      customer_email: customerInfo.customer_email || '',
      customer_phone: customerInfo.customer_phone || '',
      customer_address: customerInfo.customer_address || '',
      subtotal: serviceData.price,
      tax_amount: 0,
      final_price: serviceData.price,
      created_by: user.id
    }
    
    console.log('[createDraftEstimateWithService] Creating estimate with data:', estimateData)

    const { data, error } = await supabase
      .from('estimates')
      .insert([estimateData])
      .select()
      .single()
    
    console.log('[createDraftEstimateWithService] Insert result - Data:', data, 'Error:', error)
    
    if (error) {
      console.error('[createDraftEstimateWithService] Database error:', error)
      throw error
    }
    
    console.log('[createDraftEstimateWithService] Draft estimate created successfully:', data)
    
    // Return estimate with metadata about the AI service
    return {
      ...data,
      _aiServiceData: {
        serviceData,
        tier: serviceData.tier || 'recommended'
      }
    }
  } catch (err) {
    console.error('[createDraftEstimateWithService] Caught error:', err)
    throw err
  }
}

/**
 * Update an existing estimate  
 * Migrated from supabaseApi.js lines 1904-1930
 */
const _updateEstimate = async (estimateId, estimateData) => {
  validateRequired(estimateId, 'Estimate ID')
  
  // CRITICAL: Log the exact parameters to catch database corruption
  console.log('[API] _updateEstimate called for estimateId:', estimateId, 'with data:', estimateData)
  
  // SAFETY CHECK: Verify estimateId is a valid UUID
  if (!estimateId || typeof estimateId !== 'string' || estimateId.length < 30) {
    console.error('[API] INVALID ESTIMATE ID:', estimateId)
    throw new Error(`Invalid estimate ID: ${estimateId}`)
  }
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  // Only include columns that exist in public.estimates
  const allowedKeys = new Set([
    'client_id','job_id','estimate_number','estimate_type','customer_name',
    'customer_email','customer_phone','customer_address','items','tax_rate',
    'discount_amount','discount_type','payment_terms','status','requires_signature',
    'valid_until','custom_branding','subtotal','tax_amount',
    'final_price','total_amount','services_subtotal','client_payment_amount',
    'processing_fees_amount','deposit_amount','deposit_type','google_maps_data',
    'map_snapshot_url','signature_data','pdf_url','public_token','created_by',
    'make_line_items_optional','allow_client_scheduling',
    'client_scheduled_at','title','description','project_title','service_category',
    'notes','final_terms'
  ])
  
  const sanitized = sanitizeObject(estimateData, Array.from(allowedKeys))

  // Outputs are non-authoritative — derived from payoutBase by
  // computeClientTotal. Track them separately so we can throw loud on the
  // bare-output edit (silent no-op = user confusion). Forms send FULL
  // recomputed payloads with both base and output fields; base presence
  // triggers resync, outputs get stripped, no throw.
  const outputKeys = ['total_amount', 'final_price', 'client_payment_amount', 'processing_fees_amount']
  for (const k of outputKeys) delete sanitized[k]
  const hasOutput = outputKeys.some(k => estimateData[k] !== undefined)

  const baseChange = {}
  if (estimateData.services_subtotal !== undefined) baseChange.services_subtotal = estimateData.services_subtotal
  if (estimateData.servicesSubtotal !== undefined) baseChange.services_subtotal = estimateData.servicesSubtotal
  if (estimateData.tax_amount !== undefined) baseChange.tax_amount = estimateData.tax_amount
  if (estimateData.taxAmount !== undefined) baseChange.tax_amount = estimateData.taxAmount

  if (hasOutput && Object.keys(baseChange).length === 0 && !estimateData.__grandTotalEdit) {
    throw new Error(
      'total_amount / final_price / client_payment_amount / processing_fees_amount are derived. ' +
      'Send services_subtotal/tax_amount, or pass __grandTotalEdit:true to invert ' +
      'a new grand client total against the row\'s passThrough basis.'
    )
  }

  if (estimateData.__grandTotalEdit === true) {
    const newGrandTotal = Number(
      estimateData.total_amount ?? estimateData.final_price ?? estimateData.client_payment_amount
    ) || 0
    if (newGrandTotal <= 0) {
      throw new Error('__grandTotalEdit requires a positive total_amount / final_price / client_payment_amount')
    }
    const { data: existing, error: readErr } = await supabase
      .from('estimates')
      .select('processing_fees_amount')
      .eq('id', estimateId)
      .eq('company_id', companyId)
      .single()
    if (readErr || !existing) throw readErr || new Error('Estimate not found for grand-total edit')
    const passThrough = Number(existing.processing_fees_amount) > 0.02
    // INTENTIONAL Stripe-only inverse on shared STRIPE_FEE_RATE/
    // STRIPE_FEE_FIXED. Same notes as the invoice helper: distinct from the
    // deleted bundled-recovery inverse, gated by an explicit __grandTotalEdit
    // flag, and the only fee-rate inverse in production code.
    const newPayoutBase = passThrough
      ? Math.max(0, newGrandTotal * (1 - STRIPE_FEE_RATE) - STRIPE_FEE_FIXED)
      : newGrandTotal
    baseChange.services_subtotal = Math.round(newPayoutBase * 100) / 100
    baseChange.tax_amount = 0
  }

  if (Object.keys(baseChange).length > 0) {
    const resynced = await resyncEstimateSnapshot({ estimateId, companyId, baseChange })
    Object.assign(sanitized, resynced)
  }

  // CRITICAL: Do NOT add company_id to the UPDATE data
  // This prevents accidental updates if the WHERE clause fails
  // We only use company_id in the WHERE clause for safety
  console.log('[API] Sanitized data:', sanitized)
  console.log('[API] About to update estimate with ID:', estimateId, 'for company:', companyId)

  const { error: updateError } = await supabase
    .from('estimates')
    .update(sanitized)
    .eq('id', estimateId)
    .eq('company_id', companyId)  // ADDED: Extra safety - must match BOTH id AND company_id

  if (updateError) {
    console.error('[API] Update failed with error:', updateError)
    throw updateError
  }

  // Re-fetch with the joined client. See note in _createEstimate — the
  // UPDATE+SELECT(embed) chain hangs against this project's PostgREST/RLS
  // setup, so we split the embed into a separate SELECT here.
  const { data, error } = await supabase
    .from('estimates')
    .select(`
      *,
      client:clients(name, email, phone, sms_consent, marketing_sms_consent)
    `)
    .eq('id', estimateId)
    .eq('company_id', companyId)
    .single()

  if (error) {
    console.error('[API] Post-update fetch failed with error:', error)
    throw error
  }

  console.log('[API] Update successful, returned data:', data)
  return data
}

/**
 * Delete an estimate
 * Migrated from supabaseApi.js lines 1932-1948
 */
const _deleteEstimate = async (estimateId) => {
  validateRequired(estimateId, 'Estimate ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  const { error } = await supabase
    .from('estimates')
    .delete()
    .eq('id', estimateId)
  
  if (error) throw error
  return { success: true }
}

// Export wrapped functions
export const getEstimates = withErrorHandling(_getEstimates, 'fetch estimates', 'estimates')
export const createEstimate = withErrorHandling(_createEstimate, 'create estimate', 'estimate')
export const createDraftEstimateWithService = withErrorHandling(_createDraftEstimateWithService, 'create draft estimate with service', 'estimate')
export const updateEstimate = withErrorHandling(_updateEstimate, 'update estimate', 'estimate')
export const deleteEstimate = withErrorHandling(_deleteEstimate, 'delete estimate', 'estimate')